import { Injectable, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { ApiService } from './api.service';
import { ServiceCompanyAggregateDto, MudCheckPdfExtractionResultDto } from '@models/service-company-dto.interface';

@Injectable({
  providedIn: 'root'
})
export class ServiceCompanyService {
  private apiService = inject(ApiService);

  // Notifies listeners (e.g. the Service Company Data tab) that Mud Check Properties
  // records changed elsewhere (e.g. a PDF was uploaded/deleted from the Attachments tab),
  // so they can refresh their own copy of the data instead of showing stale state.
  // This is needed because both tabs are instantiated eagerly by mat-tab-group and each
  // keeps its own local copy of the aggregate - there is no other shared state between them.
  private readonly mudCheckPropertiesChanged$ = new Subject<number>();

  notifyMudCheckPropertiesChanged(jobId: number): void {
    this.mudCheckPropertiesChanged$.next(jobId);
  }

  onMudCheckPropertiesChanged(): Observable<number> {
    return this.mudCheckPropertiesChanged$.asObservable();
  }

  /**
   * Get the complete service company test data for a job
   */
  getServiceCompanyData(jobId: number): Observable<ServiceCompanyAggregateDto> {
    return this.apiService.get(
      `jobs/${jobId}/service-company`
    ) as Observable<ServiceCompanyAggregateDto>;
  }

  /**
   * Save/update the complete service company aggregate payload
   * Uses upsert-diff pattern on backend:
   * - New rows (Id==0) are inserted
   * - Existing rows (Id>0) are updated
   * - Missing rows are soft-deleted
   */
  saveServiceCompanyData(
    jobId: number,
    payload: ServiceCompanyAggregateDto
  ): Observable<ServiceCompanyAggregateDto> {
    return this.apiService.put(
      `jobs/${jobId}/service-company`,
      payload
    ) as Observable<ServiceCompanyAggregateDto>;
  }

  /**
   * Upload a PDF file for test data (Mud Check Properties).
   * The backend performs a best-effort extraction of known fields from the PDF text
   * and returns them alongside the stored file name - callers must prompt the user
   * to verify the extracted values before saving.
   */
  uploadTestDataPdf(
    jobId: number,
    mudCheckPropertiesId: number,
    fileName: string,
    fileContent: ArrayBuffer
  ): Observable<MudCheckPdfExtractionResultDto> {
    const formData = new FormData();
    formData.append('file', new Blob([fileContent], { type: 'application/pdf' }), fileName);
    formData.append('mudCheckPropertiesId', mudCheckPropertiesId.toString());

    // Use native HttpClient for multipart form data
    return this.apiService.post(
      `jobs/${jobId}/service-company/upload-pdf`,
      formData
    ) as Observable<MudCheckPdfExtractionResultDto>;
  }

  /**
   * Download the PDF attached to a Mud Check Properties record (stored in blob storage).
   */
  downloadMudCheckPropertiesPdf(jobId: number, mudCheckPropertiesId: number): Observable<Blob> {
    return this.apiService.getBlob(
      `jobs/${jobId}/service-company/mud-check-properties/${mudCheckPropertiesId}/pdf`
    );
  }

  /**
   * Re-read and re-extract data from a previously uploaded PDF file,
   * overwriting all current field values with newly extracted data.
   */
  rereadMudCheckPropertiesPdf(jobId: number, mudCheckPropertiesId: number): Observable<MudCheckPdfExtractionResultDto> {
    return this.apiService.post(
      `jobs/${jobId}/service-company/mud-check-properties/${mudCheckPropertiesId}/reread-pdf`,
      {}
    ) as Observable<MudCheckPdfExtractionResultDto>;
  }

  /**
   * Upload a PDF file for Mud Check Properties (Service Company test data).
   * The backend performs a best-effort extraction of known fields from the PDF text
   * and returns them alongside the stored file name - callers must prompt the user
   * to verify the extracted values before saving. Pass mudCheckPropertiesId=0 to create
   * a new record; a positive id attaches/re-parses the PDF against an existing record.
   */
  uploadMudCheckPropertiesPdf(jobId: number, mudCheckPropertiesId: number, file: File): Observable<MudCheckPdfExtractionResultDto> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('mudCheckPropertiesId', mudCheckPropertiesId.toString());

    return this.apiService.post(
      `jobs/${jobId}/service-company/upload-pdf`,
      formData
    ) as Observable<MudCheckPdfExtractionResultDto>;
  }

  /**
   * Delete a Mud Check Properties record and remove its PDF from blob storage.
   */
  deleteMudCheckPropertiesRecord(jobId: number, mudCheckPropertiesId: number): Observable<void> {
    return this.apiService.delete(
      `jobs/${jobId}/service-company/mud-check-properties/${mudCheckPropertiesId}`
    );
  }
}
